/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  useEffect,
  useState,
  MutableRefObject,
  useCallback,
  useRef,
} from "react";
import { useUpdateEffect } from "react-use";
import { validate as isValidUUID } from "uuid";
import {
  Item,
  SubItem,
  ListWithInputsItem,
  ListWithInputsSelections,
} from "./types";
import {
  transformOptionsToCheckboxes,
  transformOptionsToInputs,
} from "./utils";

type Props = {
  selectableItems: Item[];
  selectedOptions: string[];
  onMultiSelect: (ids: string[]) => void;
  selectedSubOptions?: string[];
  onMultiSubSelect?: (ids: string[]) => void;
};

export function useSelectableListFilter({
  selectableItems,
  selectedOptions,
  onMultiSelect,
  selectedSubOptions,
  onMultiSubSelect,
}: Props) {
  const [selectedItems, setSelectedItems] = useState(selectedOptions);
  const [selectedSubItems, setSelectedSubItems] = useState(
    selectedSubOptions ?? []
  );
  const [search, setSearch] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Item[]>([]);

  useUpdateEffect(() => {
    setSelectedItems(selectedOptions);
  }, [JSON.stringify(selectedOptions)]);

  useUpdateEffect(() => {
    onMultiSelect(selectedItems ?? []);
  }, [selectedItems]);

  useUpdateEffect(() => {
    setSelectedSubItems(selectedSubOptions ?? []);
  }, [JSON.stringify(selectedSubOptions ?? [])]);

  useUpdateEffect(() => {
    onMultiSubSelect?.(selectedSubItems ?? []);
  }, [selectedSubItems]);

  useEffect(() => {
    setFilteredOptions(
      selectableItems
        .map((item) => {
          return {
            ...item,
            subCheckboxes: item.subCheckboxes?.filter(({ label, value }) => {
              // search by value only if valid uuid, like valid plan id
              if (isValidUUID(search)) {
                return (
                  label?.toLowerCase().includes(search) ||
                  value?.toLowerCase().includes(search)
                );
              }
              return label?.toLowerCase().includes(search.toLowerCase());
            }),
          };
        })
        .filter(({ label, value, subCheckboxes }) => {
          // search by value only if valid uuid, like valid plan id
          if (isValidUUID(search)) {
            return (
              label?.toLowerCase().includes(search) ||
              value?.toLowerCase().includes(search)
            );
          }
          return (
            label?.toLowerCase().includes(search.toLowerCase()) ||
            (subCheckboxes && subCheckboxes.length > 0)
          );
        })
    );
  }, [search, selectableItems]);

  const onApply = (item: string, allSubItems: SubItem[]) => {
    const allSubValues = allSubItems
      .filter((subItem) => !!subItem)
      .map((subItem) => subItem.value);
    const itemClearedZeros = item.replace(/^0+/, "");
    const isItemSelected =
      selectedItems.includes(item) || selectedItems.includes(itemClearedZeros);

    if (isItemSelected) {
      setSelectedItems(removeItemFromState(item, itemClearedZeros));
      setSelectedSubItems((prev) =>
        prev.filter((storedSubItem) => !allSubValues.includes(storedSubItem))
      );
    } else {
      setSelectedItems((prev) => [...prev, item]);
      setSelectedSubItems((prev) => {
        const removedDuplicates = new Set([
          ...prev,
          ...(allSubValues as string[]),
        ]);

        return Array.from(removedDuplicates);
      });
    }
  };

  const onSubItemApply = (
    item: string,
    parentItem: string,
    allSubItems: SubItem[]
  ) => {
    const allSubValues = allSubItems.map((subItem) => subItem.value);
    const itemClearedZeros = replaceZeros(item);
    const parentItemClearedZeros = replaceZeros(parentItem);
    const isSubItemSelected =
      selectedSubItems.includes(item) ||
      selectedSubItems.includes(itemClearedZeros);

    if (isSubItemSelected) {
      setSelectedSubItems(removeItemFromState(item, itemClearedZeros));
      setSelectedItems(removeItemFromState(parentItem, parentItemClearedZeros));
    } else {
      setSelectedSubItems((prev) => [...prev, item]);
      // check if all subItems are selected
      const updatedSelectedSubItems = [...selectedSubItems, item];
      const selectedSubItemsOfItem = allSubValues.filter((subItem) =>
        updatedSelectedSubItems.includes(subItem ?? "")
      );

      if (selectedSubItemsOfItem.length === allSubItems.length) {
        setSelectedItems((prev) => [...prev, parentItem]);
      }
    }
  };

  const areAllFilteredOptionsSelected = filteredOptions.every((item) => {
    if (!item.value) {
      return true;
    }

    return includesValue(selectedItems, item.value);
  });

  const selectAllFilteredOptions = () => {
    const filteredValues = getFilteredValues(filteredOptions);

    setSelectedItems((prev) => mergeUniqueValues(prev, filteredValues.items));
    setSelectedSubItems((prev) =>
      mergeUniqueValues(prev, filteredValues.subItems)
    );
  };

  const deselectAllFilteredOptions = () => {
    const filteredValues = getFilteredValues(filteredOptions);

    setSelectedItems((prev) => removeValues(prev, filteredValues.items));
    setSelectedSubItems((prev) => removeValues(prev, filteredValues.subItems));
  };

  return {
    selectedItems,
    selectedSubItems,
    filteredOptions,
    areAllFilteredOptionsSelected,
    setSearch,
    onApply,
    onSubItemApply,
    selectAllFilteredOptions,
    deselectAllFilteredOptions,
  };
}

function replaceZeros(item: string) {
  return item.replace(/^0+/, "");
}

function includesValue(values: string[], value: string) {
  const normalizedValue = replaceZeros(value);

  return values.some(
    (storedValue) => replaceZeros(storedValue) === normalizedValue
  );
}

function mergeUniqueValues(existingValues: string[], newValues: string[]) {
  return newValues.reduce((mergedValues, value) => {
    if (includesValue(mergedValues, value)) {
      return mergedValues;
    }

    return [...mergedValues, value];
  }, existingValues);
}

function removeValues(existingValues: string[], valuesToRemove: string[]) {
  const normalizedValuesToRemove = new Set(valuesToRemove.map(replaceZeros));

  return existingValues.filter(
    (storedValue) => !normalizedValuesToRemove.has(replaceZeros(storedValue))
  );
}

function getFilteredValues(filteredOptions: Item[]) {
  return filteredOptions.reduce(
    (acc, item) => {
      const items = item.value ? [...acc.items, item.value] : acc.items;
      const subItems =
        item.subCheckboxes?.reduce((values, subItem) => {
          if (!subItem.value) {
            return values;
          }

          return [...values, subItem.value];
        }, acc.subItems) ?? acc.subItems;

      return { items, subItems };
    },
    { items: [] as string[], subItems: [] as string[] }
  );
}

function removeItemFromState(item: string, itemClearedZeros: string) {
  return (prev: string[]) =>
    prev.filter(
      (storedItem) => storedItem !== item && storedItem !== itemClearedZeros
    );
}

type ListWithInputsFilterProps = {
  selectableItems: ListWithInputsItem[];
  selectedOptions: ListWithInputsSelections;
  onMultiSelect: (selections: ListWithInputsSelections) => void;
};

export function useSelectableListWithInputsFilter({
  selectableItems,
  selectedOptions,
  onMultiSelect,
}: ListWithInputsFilterProps) {
  const [checkboxes, setCheckboxes] = useState<Record<number, boolean>>(() => {
    return transformOptionsToCheckboxes(selectedOptions, selectableItems);
  });

  const [inputs, setInputs] = useState<Record<number, string[]>>(() => {
    return transformOptionsToInputs(selectedOptions, selectableItems);
  });

  const [search, setSearch] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<ListWithInputsItem[]>(
    []
  );

  // Track previous selectedOptions to detect actual changes
  const previousSelectedOptions =
    useRef<ListWithInputsSelections>(selectedOptions);

  // Sync with external selectedOptions changes (deep comparison to prevent infinite loop)
  useUpdateEffect(() => {
    const sortedCurrentCheckboxes = selectedOptions.checkboxes
      ?.slice()
      .sort((a, b) => a.localeCompare(b));
    const sortedPreviousCheckboxes = previousSelectedOptions.current.checkboxes
      ?.slice()
      .sort((a, b) => a.localeCompare(b));

    const checkboxesChanged =
      JSON.stringify(sortedCurrentCheckboxes) !==
      JSON.stringify(sortedPreviousCheckboxes);

    const inputsChanged =
      JSON.stringify(selectedOptions.inputsByCheckbox) !==
      JSON.stringify(previousSelectedOptions.current.inputsByCheckbox);

    if (checkboxesChanged || inputsChanged) {
      previousSelectedOptions.current = selectedOptions;
      setCheckboxes(
        transformOptionsToCheckboxes(selectedOptions, selectableItems)
      );
      setInputs(transformOptionsToInputs(selectedOptions, selectableItems));
    }
  }, [selectedOptions, selectableItems]);

  useUpdateEffect(() => {
    const selectedCheckboxValues = Object.entries(checkboxes)
      .map((entry) => {
        const [checkboxIndex, isSelected] = entry;

        if (isSelected) {
          return selectableItems[Number(checkboxIndex)].value;
        }

        return null;
      })
      .filter(
        (checkbox): checkbox is string =>
          checkbox !== undefined && checkbox !== null
      );

    const selectedInputsByCheckboxValues: Record<string, string[]> =
      Object.entries(inputs).reduce((acc, entry) => {
        const [checkboxIndex, selectedInputs] = entry;

        const checkboxValue = selectableItems[Number(checkboxIndex)]
          .value as string; // always present

        return {
          ...acc,
          [checkboxValue]: selectedInputs,
        };
      }, {});

    onMultiSelect({
      checkboxes: selectedCheckboxValues,
      inputsByCheckbox: selectedInputsByCheckboxValues,
    });
  }, [inputs, checkboxes]);

  useEffect(() => {
    setFilteredOptions(
      selectableItems.filter((item) => {
        return (
          item?.label?.toLowerCase().includes(search.toLowerCase()) ||
          item?.value?.toLowerCase().includes(search.toLowerCase())
        );
      })
    );
  }, [search, selectableItems]);

  const isCheckboxSelected = (index: number) => {
    return checkboxes[index] ?? false;
  };

  return {
    inputs,
    checkboxes,
    filteredOptions,
    setSearch,
    setInputs,
    setCheckboxes,
    isCheckboxSelected,
  };
}

export const useResize = (
  ref: MutableRefObject<HTMLDivElement | null>,
  setWidth: (value: number) => void
) => {
  const handleResize = useCallback(() => {
    setWidth(ref?.current?.offsetWidth ?? 0);
  }, [ref, setWidth]);

  useEffect(() => {
    window.addEventListener("load", handleResize);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("load", handleResize);
      window.removeEventListener("resize", handleResize);
    };
  }, [ref, handleResize]);
};
